import { Spinner } from '@cimplico/components';
import ThreadRow from '../../components/threads/ThreadRow';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { useUserContext } from '../../providers/UserProvider';
import { useClientQueries } from '../../queries/use-client-queries';
import { ClientQueryOverview, ClientQueryStatusEnum } from '../../types/client-query';
import ClientQueryStatus from './ClientQueryStatus';
import { getQueryContext } from './utils';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { InboxIcon } from '@heroicons/react/24/outline';

type ClientQueriesTableProps = {
    checkedRows?: string[];
    showTempRow: boolean;
    onRowSelect: (id: string) => void;
    onRowChecked: (id: string, title: string, context: string) => void;
    onRowActive: (id: string) => void;
    onRowResolve: (id: string) => void;
    onRowDelete: (id: string) => void;
    onRowEdit: (clientQuery: ClientQueryOverview) => void;
    filter?: string;
    sort?: string;
    search?: string;
    onNew?: () => void;
    hideEmptyState?: boolean;
};

function ClientQueriesTable({
    checkedRows,
    showTempRow,
    onRowSelect,
    onRowChecked,
    onRowActive,
    onRowResolve,
    onRowDelete,
    onRowEdit,
    filter,
    sort,
    search,
    onNew,
    hideEmptyState,
}: ClientQueriesTableProps): JSX.Element {
    const { id: workpaperId, readOnly } = useWorkpaperContext();
    const user = useUserContext();

    const clientQueriesQuery = useClientQueries(workpaperId, { filter, search, sort });

    function handleActionClick(action: string, query: ClientQueryOverview): void {
        if (action === 'ACTIVE') {
            onRowActive(query.id);
        }
        if (action === 'RESOLVE') {
            onRowResolve(query.id);
        }
        if (action === 'DELETE') {
            onRowDelete(query.id);
        }
        if (action === 'EDIT') {
            onRowEdit(query);
        }
    }

    if (clientQueriesQuery.isLoading) {
        return <></>;
    }

    if (!clientQueriesQuery.isSuccess) {
        return <p>Error!</p>;
    }

    const clientQueries = clientQueriesQuery.data.data;
    const isSearching = Boolean(search);
    return (
        <div className="relative">
            {clientQueriesQuery.isFetching && (
                <div className="pointer-events-none absolute top-0 left-0 w-full h-full z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}

            <div>
                {clientQueries.length === 0 && !hideEmptyState && (
                    <div className="mt-16">
                        {isSearching ? (
                            <NoResults search={search || ''} />
                        ) : (
                            <EmptyStateNew
                                icon={<InboxIcon />}
                                actionText="New query"
                                helpLink="https://learn.cimplico.com/docs/client-queries-overview#create-query"
                                helpText="Draft client queries as you prepare and review worksheets."
                                title="Draft a query"
                                disableActionButton={readOnly}
                                onClick={(): void => onNew && onNew()}
                            />
                        )}
                    </div>
                )}
                <div className="mt-4">
                    {showTempRow && (
                        <ThreadRow
                            disableAllActions
                            createdAt={new Date()}
                            createdBy={user}
                            title="New thread"
                            statusBadge={<ClientQueryStatus status={ClientQueryStatusEnum.DRAFT} />}
                            id=""
                            replies={0}
                        />
                    )}
                    {clientQueries.map((query) => {
                        const canEditOrDelete =
                            query.status === ClientQueryStatusEnum.DRAFT ||
                            (query.status === ClientQueryStatusEnum.ACTIVE && query.createdBy.id === user.id);

                        return (
                            <ThreadRow
                                disableAllActions={readOnly}
                                showCheckbox
                                checked={checkedRows?.includes(query.id)}
                                onSelect={onRowSelect}
                                onChecked={onRowChecked}
                                disableCheckbox={readOnly || query.status !== ClientQueryStatusEnum.DRAFT}
                                key={query.id}
                                createdBy={query.createdBy}
                                createdAt={query.createdAt}
                                id={query.id}
                                replies={query.replies.length}
                                title={query.rawQuery}
                                context={getQueryContext(query)}
                                actions={[
                                    {
                                        label: 'Mark as resolved',
                                        value: 'RESOLVE',
                                        hidden: query.status !== ClientQueryStatusEnum.ACTIVE,
                                    },
                                    {
                                        label: 'Mark as active',
                                        value: 'ACTIVE',
                                        hidden: query.status === ClientQueryStatusEnum.ACTIVE,
                                    },
                                    {
                                        label: 'Delete',
                                        value: 'DELETE',
                                        hidden: !canEditOrDelete,
                                    },
                                    {
                                        label: 'Edit',
                                        value: 'EDIT',
                                        hidden: !canEditOrDelete,
                                    },
                                ]}
                                statusBadge={<ClientQueryStatus status={query.status} />}
                                onActionsClick={(action: string): void => {
                                    handleActionClick(action, query);
                                }}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default ClientQueriesTable;
